import React from 'react';
import {
    Box, Button, Chip, Divider, Paper, Stack, Typography
} from '@mui/material';
import {
    Assessment as AssessmentIcon,
    Email as EmailIcon,
    Group as GroupIcon,
    LockReset as LockResetIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Security as SecurityIcon,
    VpnKey as KeyIcon,
    WorkOutline as WorkIcon
} from '@mui/icons-material';

const humanizeCode = (value) => {
    if (!value) return 'Not configured';
    return String(value)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase())
        .replace(/\bHr\b/g, 'HR')
        .replace(/\bOt\b/g, 'OT');
};

const cardStyle = {
    p: { xs: 2, md: 2.5 },
    borderRadius: 3,
    borderColor: '#e2e8f0',
    boxShadow: 'none',
    height: '100%',
    boxSizing: 'border-box'
};

const PolicyCard = ({ icon: Icon, title, children }) => (
    <Paper variant="outlined" sx={cardStyle}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#e8f5e9', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {title}
            </Typography>
        </Stack>
        <Stack divider={<Divider flexItem />} spacing={0}>
            {children}
        </Stack>
    </Paper>
);

const PolicyRow = ({ label, value, status = 'neutral' }) => {
    const chipColors = {
        success: { color: '#166534', bgcolor: '#dcfce7', borderColor: '#bbf7d0' },
        warning: { color: '#92400e', bgcolor: '#fef3c7', borderColor: '#fde68a' },
        neutral: { color: '#334155', bgcolor: '#f8fafc', borderColor: '#e2e8f0' }
    };

    return (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ py: 1.25, minHeight: 42 }}>
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                {label}
            </Typography>
            <Chip
                size="small"
                label={value || 'Not configured'}
                variant="outlined"
                sx={{ ...chipColors[status], fontWeight: 700, maxWidth: '65%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
        </Stack>
    );
};

const AccountField = ({ icon: Icon, label, value }) => (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#e8f5e9', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, overflowWrap: 'anywhere', mt: 0.25 }}>
                {value || 'Not configured'}
            </Typography>
        </Box>
    </Stack>
);

const StaffAccessOverview = ({ staff, canManage, onResetSecurity }) => {
    const overtimeEnabled = Boolean(staff.ot_eligible);
    const webClockEnabled = Boolean(staff.web_clock_allowed);

    return (
        <Box>
            <Box sx={{ mb: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    Access &amp; Policy Overview
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                    Employment rules, attendance controls, approval routing, and account access.
                </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                <PolicyCard icon={WorkIcon} title="Work & Attendance">
                    <PolicyRow label="Work modality" value={staff.work_type === 'shift_based' ? 'Shift System' : (staff.allow_split_shift ? 'Office Split Shift' : 'Standard Office')} />
                    <PolicyRow label="Attendance approval" value={staff.auto_approve_manual_attendance ? 'Automatic' : 'Approval Required'} status={staff.auto_approve_manual_attendance ? 'success' : 'neutral'} />
                    <PolicyRow label="Photo evidence" value={staff.is_photo_required !== 0 ? 'Required' : 'Optional'} status={staff.is_photo_required !== 0 ? 'success' : 'neutral'} />
                </PolicyCard>

                <PolicyCard icon={AssessmentIcon} title="Overtime & Payroll">
                    <PolicyRow label="Overtime" value={overtimeEnabled ? 'Eligible' : 'Not Eligible'} status={overtimeEnabled ? 'success' : 'neutral'} />
                    <PolicyRow label="OT policy" value={overtimeEnabled ? humanizeCode(staff.ot_policy_code) : 'Not applicable'} />
                    <PolicyRow label="OT rate profile" value={overtimeEnabled ? (staff.ot_rate_profile || 'Not configured') : 'Not applicable'} />
                    <PolicyRow label="Payroll processing" value={staff.exclude_from_payroll ? 'Excluded' : 'Included'} status={staff.exclude_from_payroll ? 'warning' : 'success'} />
                </PolicyCard>

                <PolicyCard icon={ScheduleIcon} title="Clocking Controls">
                    <PolicyRow label="Web clock" value={webClockEnabled ? 'Allowed' : 'Mobile App Only'} status={webClockEnabled ? 'success' : 'neutral'} />
                    <PolicyRow label="Browser geofence" value={staff.web_geofence_required ? 'Required' : 'Not Required'} status={staff.web_geofence_required ? 'success' : 'neutral'} />
                </PolicyCard>

                <PolicyCard icon={GroupIcon} title="Approval Routing">
                    <PolicyRow label="Reporting supervisor (L1)" value={staff.manager_name || 'Not assigned'} />
                    <PolicyRow label="HR approver (L2)" value={staff.hr_name || 'Not assigned'} />
                </PolicyCard>

                <Paper variant="outlined" sx={{ ...cardStyle, gridColumn: { xs: 'auto', md: '1 / -1' }, bgcolor: '#f8fafc' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2.5 }}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#e8f5e9', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <SecurityIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Account Access
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>Login identity and assigned system role</Typography>
                            </Box>
                        </Stack>
                        {canManage && (
                            <Button variant="outlined" size="small" startIcon={<LockResetIcon />} onClick={onResetSecurity} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
                                Reset Security
                            </Button>
                        )}
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2.5 }}>
                        <AccountField icon={PersonIcon} label="System Username" value={staff.username} />
                        <AccountField icon={EmailIcon} label="Work Email" value={staff.email} />
                        <AccountField icon={KeyIcon} label="System Role" value={humanizeCode(staff.role)} />
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default StaffAccessOverview;
